import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Microscope,
  Plus,
  Trash2,
  Loader2,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Save,
  Pencil,
} from 'lucide-react';
import { toast } from 'sonner';
import complianceService from '../services/complianceService';

const TEST_STATUSES = ['Pending', 'Pass', 'Fail'];

const statusBadge = (status) => {
  const s = status?.toLowerCase();
  if (s === 'pass') return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1 inline" />Pass</Badge>;
  if (s === 'fail') return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1 inline" />Fail</Badge>;
  return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1 inline" />Pending</Badge>;
};

export default function LabCoaManagementPage() {
  const [products, setProducts] = useState([]);
  const [batches, setBatches] = useState([]);
  const [labTests, setLabTests] = useState([]);

  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedBatchId, setSelectedBatchId] = useState('');

  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);
  const [isLoadingTests, setIsLoadingTests] = useState(false);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [editTest, setEditTest] = useState(null);
  const [newTestStatus, setNewTestStatus] = useState('Pending');

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    if (selectedProductId) {
      setSelectedBatchId('');
      setBatches([]);
      setLabTests([]);
      fetchBatches(selectedProductId);
    }
  }, [selectedProductId]);

  useEffect(() => {
    if (selectedBatchId) {
      fetchLabTests(selectedBatchId);
    } else {
      setLabTests([]);
    }
  }, [selectedBatchId]);

  const fetchProducts = async () => {
    setIsLoadingProducts(true);
    try {
      const data = await complianceService.getProducts();
      setProducts(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load products');
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const fetchBatches = async (productId) => {
    setIsLoadingBatches(true);
    try {
      const data = await complianceService.getBatches(productId);
      setBatches(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load batches');
      setBatches([]);
    } finally {
      setIsLoadingBatches(false);
    }
  };

  const fetchLabTests = async (batchId) => {
    setIsLoadingTests(true);
    try {
      const data = await complianceService.getLabTests(batchId);
      setLabTests(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load lab tests');
      setLabTests([]);
    } finally {
      setIsLoadingTests(false);
    }
  };

  const handleAddTest = async (e) => {
    e.preventDefault();
    if (!selectedBatchId) return;
    setIsSaving(true);
    try {
      await complianceService.createLabTest({
        batchId: Number(selectedBatchId),
        testStatus: newTestStatus,
      });
      toast.success('Lab test added');
      setIsAddDialogOpen(false);
      setNewTestStatus('Pending');
      fetchLabTests(selectedBatchId);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to add lab test');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditTest = async (e) => {
    e.preventDefault();
    if (!editTest) return;
    setIsSaving(true);
    try {
      await complianceService.updateLabTest(editTest.labTestId, {
        batchId: editTest.batchId,
        testStatus: editTest.testStatus,
      });
      toast.success('Lab test updated');
      setIsEditDialogOpen(false);
      setEditTest(null);
      fetchLabTests(selectedBatchId);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to update lab test');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTest = async (id) => {
    if (!window.confirm('Delete this lab test record?')) return;
    try {
      await complianceService.deleteLabTest(id);
      toast.success('Lab test deleted');
      fetchLabTests(selectedBatchId);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to delete lab test');
    }
  };

  const openEdit = (test) => {
    setEditTest({ ...test });
    setIsEditDialogOpen(true);
  };

  const selectedProduct = products.find(p => String(p.productId ?? p.id) === String(selectedProductId));
  const selectedBatch = batches.find(b => String(b.batchId) === String(selectedBatchId));

  const passCount = labTests.filter(t => t.testStatus?.toLowerCase() === 'pass').length;
  const failCount = labTests.filter(t => t.testStatus?.toLowerCase() === 'fail').length;
  const pendingCount = labTests.filter(t => t.testStatus?.toLowerCase() === 'pending').length;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Lab / COA Management</h1>
            <p className="text-muted-foreground mt-1">Manage lab test results per product batch</p>
          </div>
          <div className="flex gap-2">
            {selectedBatchId && (
              <Button variant="outline" size="sm" onClick={() => fetchLabTests(selectedBatchId)}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            )}
            <Button onClick={() => setIsAddDialogOpen(true)} disabled={!selectedBatchId}>
              <Plus className="h-4 w-4 mr-2" />
              Add Lab Test
            </Button>
          </div>
        </div>

        {/* Selectors */}
        <Card>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Product</Label>
                {isLoadingProducts ? (
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading products...
                  </div>
                ) : (
                  <Select value={selectedProductId} onValueChange={setSelectedProductId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a product" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map(p => (
                        <SelectItem key={p.productId ?? p.id} value={String(p.productId ?? p.id)}>
                          {p.productName ?? p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              <div className="space-y-2">
                <Label>Batch</Label>
                {isLoadingBatches ? (
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading batches...
                  </div>
                ) : (
                  <Select
                    value={selectedBatchId}
                    onValueChange={setSelectedBatchId}
                    disabled={!selectedProductId || batches.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          !selectedProductId
                            ? 'Select a product first'
                            : batches.length === 0
                            ? 'No batches found'
                            : 'Select a batch'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {batches.map(b => (
                        <SelectItem key={b.batchId} value={String(b.batchId)}>
                          {b.batchNumber} — {b.batchStatus}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Batch selected — show data */}
        {selectedBatchId && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Passed</p>
                    <p className="text-3xl font-bold text-green-600 mt-1">{passCount}</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Failed</p>
                    <p className="text-3xl font-bold text-red-600 mt-1">{failCount}</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg">
                    <XCircle className="h-6 w-6 text-red-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pending</p>
                    <p className="text-3xl font-bold text-yellow-600 mt-1">{pendingCount}</p>
                  </div>
                  <div className="p-3 bg-yellow-50 rounded-lg">
                    <Clock className="h-6 w-6 text-yellow-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Lab Tests Table */}
            <Card>
              <CardHeader>
                <CardTitle>Lab Test Records</CardTitle>
                <CardDescription>
                  Batch: {selectedBatch?.batchNumber} — {selectedProduct?.productName ?? selectedProduct?.name}
                  {' '}({labTests.length} test{labTests.length !== 1 ? 's' : ''})
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingTests ? (
                  <div className="flex items-center justify-center h-40">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : labTests.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Microscope className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No lab tests recorded yet. Click "Add Lab Test" to get started.</p>
                  </div>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Test ID</TableHead>
                          <TableHead>Batch ID</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {labTests.map((t) => (
                          <TableRow key={t.labTestId}>
                            <TableCell className="font-medium">#{t.labTestId}</TableCell>
                            <TableCell className="text-muted-foreground">#{t.batchId}</TableCell>
                            <TableCell>{statusBadge(t.testStatus)}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => openEdit(t)}>
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteTest(t.labTestId)}
                                  className="text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Empty state */}
        {!selectedProductId && !isLoadingProducts && (
          <div className="text-center py-20 text-muted-foreground">
            <Microscope className="h-16 w-16 mx-auto mb-4 opacity-20" />
            <p className="text-lg">Select a product to manage its lab tests</p>
          </div>
        )}
      </div>

      {/* Add Lab Test Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Lab Test</DialogTitle>
            <DialogDescription>
              Add a new lab test record for batch <strong>{selectedBatch?.batchNumber}</strong>
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddTest} className="space-y-4">
            <div className="space-y-1">
              <Label>Test Status *</Label>
              <Select value={newTestStatus} onValueChange={setNewTestStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TEST_STATUSES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" className="flex-1" disabled={isSaving}>
                {isSaving
                  ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  : <Plus className="h-4 w-4 mr-2" />}
                Add Test
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Lab Test Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Lab Test #{editTest?.labTestId}</DialogTitle>
            <DialogDescription>Update the test status</DialogDescription>
          </DialogHeader>
          {editTest && (
            <form onSubmit={handleEditTest} className="space-y-4">
              <div className="space-y-1">
                <Label>Test Status *</Label>
                <Select
                  value={editTest.testStatus}
                  onValueChange={v => setEditTest(p => ({ ...p, testStatus: v }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TEST_STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 pt-2">
                <Button type="submit" className="flex-1" disabled={isSaving}>
                  {isSaving
                    ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    : <Save className="h-4 w-4 mr-2" />}
                  Save Changes
                </Button>
                <Button type="button" variant="outline" className="flex-1" onClick={() => setIsEditDialogOpen(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
